import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { networkManager } from '../api/NetworkManager';
import { Plus, Loader2, Calendar, FlaskConical } from 'lucide-react';

interface EventItem {
  eventId: string;
  name: string;
  status: string;
  startDate: string;
  endDate: string;
  themes: Array<{ id: string; name: string }>;
  isTest?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  submission_locked: 'bg-orange-100 text-orange-700',
  live_demo: 'bg-purple-100 text-purple-700',
  judging: 'bg-red-100 text-red-700',
  completed: 'bg-green-100 text-green-700',
};

const SHOW_TEST_KEY = 'byte_show_test_events';

export function EventListPage() {
  const { can } = useAuth();
  const isAdmin = can('admin');
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTest, setShowTest] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SHOW_TEST_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    setLoading(true);
    const qs = isAdmin && showTest ? '?includeTest=true' : '';
    networkManager
      .get<{ status: boolean; data: EventItem[] }>(`/api/events${qs}`)
      .then((res) => setEvents(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isAdmin, showTest]);

  function toggleShowTest() {
    const next = !showTest;
    setShowTest(next);
    try {
      localStorage.setItem(SHOW_TEST_KEY, next ? 'true' : 'false');
    } catch { /* ignore */ }
  }

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-3xl font-bold text-ink">Events</h1>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <button
              onClick={toggleShowTest}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                showTest
                  ? 'border-purple-300 bg-purple-50 text-purple-700'
                  : 'border-ring bg-white text-ink-secondary hover:bg-gray-50'
              }`}
              title="Toggle visibility of test events"
            >
              <FlaskConical className="h-3.5 w-3.5" />
              {showTest ? 'Hide test events' : 'Show test events'}
            </button>
          )}
          {can('event:create') && (
            <Link to="/events/new" className="btn-primary inline-flex items-center gap-1.5 text-sm">
              <Plus className="h-4 w-4" /> Create Event
            </Link>
          )}
        </div>
      </div>

      {loading ? (
        <div className="mt-12 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
        </div>
      ) : events.length === 0 ? (
        <div className="mt-12 text-center">
          <Calendar className="mx-auto h-12 w-12 text-ink-tertiary" />
          <p className="mt-4 text-ink-secondary">No events yet.</p>
          {can('event:create') && (
            <Link to="/events/new" className="btn-primary mt-4 inline-flex">
              Create your first event
            </Link>
          )}
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((event) => (
            <Link
              key={event.eventId}
              to={`/events/${event.eventId}`}
              className={`card p-5 transition-shadow hover:shadow-card-hover ${event.isTest ? 'border-purple-200' : ''}`}
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-ink">{event.name}</h3>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className={`badge ${STATUS_COLORS[event.status] ?? 'bg-gray-100 text-gray-700'}`}>
                    {event.status.replace(/_/g, ' ')}
                  </span>
                  {event.isTest && (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-purple-100 px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider text-purple-700">
                      <FlaskConical className="h-2.5 w-2.5" /> Test
                    </span>
                  )}
                </div>
              </div>
              {event.startDate && (
                <p className="mt-2 text-sm text-ink-secondary">
                  {new Date(event.startDate).toLocaleDateString()} –{' '}
                  {event.endDate ? new Date(event.endDate).toLocaleDateString() : '...'}
                </p>
              )}
              {event.themes?.length > 0 && (
                <p className="mt-1 text-xs text-ink-tertiary">
                  {event.themes.length} theme{event.themes.length !== 1 ? 's' : ''}
                </p>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
